//
// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Item from "@material-ui/core/Grid";
import ExtractButton from "src/components/loader/buttons/ExtractButton";
import DownloadButton from "src/components/loader/buttons/DownloadButton";
import TreeView from "eai-configurator-ui/components/base/TreeView";
import ModuleTreeView from "src/components/loader/tree/ModuleTreeView";
import Paper from "@material-ui/core/Paper";
import Accordion from "@material-ui/core/ExpansionPanel";
import AccordionSummary from "@material-ui/core/ExpansionPanelSummary";
import AccordionDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ConfigurationCheckBox from "eai-configurator-ui/components/configuration/base/ConfigurationCheckBox";
import ConfigurationComboBox from "eai-configurator-ui/components/configuration/base/ConfigurationComboBox";
import ConfigurationDatePicker from "eai-configurator-ui/components/configuration/base/ConfigurationDatePicker";
import ConfigurationTextField from "eai-configurator-ui/components/configuration/base/ConfigurationTextField";
import ErrorMessageDialog, {useErrorMessageDialog,} from "eai-configurator-ui/components/configuration/dialog/ErrorMessageDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  textField: {
    width: "100%",
  },
  error: {
    color: "red",
  },
  checkbox: {
    paddingLeft: 0,
  },
  paper: {
    padding: 10,
  },
  button: {
    margin: 4,
  },
  expandablePanelIcon: {
    margin: 4,
  },
  expandablePanel: {
    paddingLeft: 10,
  },
}));
const ExtractPage = ({navigateToResults}) => {
  const classes = useStyles();
  const [errorMessage, setErrorMessage] = useErrorMessageDialog();

  const path = "loader:extract";
  return (
    <React.Fragment>
      <div>
        <Typography variant="body2" color="textSecondary">
          <h5>Extract from P6 into Excel files</h5>
          <hr/>
        </Typography>

        <Box m={1} display="flex" flexDirection="row" justifyContent="flex-end">
          <ExtractButton
            className={classes.button}
            handleSuccess={navigateToResults}
          />
          <DownloadButton
            label={"Download Example"}
            className={classes.button}
            fileName={"sample.xlsx"}
          />
          <DownloadButton
            label={"Download Template"}
            className={classes.button}
            fileName={"template.xlsx"}
          />
        </Box>

        <div>
          <ErrorMessageDialog {...errorMessage} />
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              aria-controls="advanced-options"
              id="advanced-options"
              className={classes.expandablePanel}
            >
              <Typography variant="body2" color="textSecondary">
                <h6>
                  <ExpandMoreIcon
                    size={20}
                    className={classes.expandablePanelIcon}
                  />
                  More Options
                </h6>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div style={{flexGrow: 1}}>
                <Paper className={classes.paper}>
                  <Typography variant="body2" color="textSecondary">
                    <h6>Codes and UDFs</h6>
                  </Typography>

                  <Typography variant="caption" color="textSecondary">
                    Use this area to control if codes and UDFs should be extracted with the P6-Loader.
                    <br/>
                    If you don't need to work with codes and UDFs, unchecking the corresponding options will make the extraction run faster.
                  </Typography>

                  <Grid container style={{marginTop: 10}}>
                    <Item xs={3}>
                      <ConfigurationCheckBox path={`${path}:extractCodeAssignment`}/>
                    </Item>
                    <Item xs={3}>
                      <ConfigurationCheckBox path={`${path}:extractUdfs`}/>
                    </Item>
                  </Grid>
                </Paper>

                <hr/>

                <Paper className={classes.paper}>
                  <Typography variant="body2" color="textSecondary">
                    <h6>Duration Options</h6>
                  </Typography>

                  <Typography variant="caption" color="textSecondary">
                    Use this area to control the duration type for exported duration fields with the P6-Loader.
                  </Typography>

                  <Grid container style={{marginTop: 10}}>
                    <Item xs={6}>
                      <ConfigurationComboBox path={`${path}:durationType`}/>
                    </Item>
                  </Grid>
                </Paper>

                <hr/>

                <Paper className={classes.paper}>
                  <Typography variant="body2" color="textSecondary">
                    <h6>Spread Options</h6>
                  </Typography>

                  <Typography variant="caption" color="textSecondary">
                    Use the override start and finish to indicate the time period
                    the spread should be generated for.
                    <br/>
                    If not provided, the spreads will be generated for the entire
                    length of the project(s).
                  </Typography>

                  <Grid container style={{marginTop: 10}}>
                    <Item xs={6}>
                      <ConfigurationComboBox path={`${path}:spreadFrequency`}/>
                    </Item>
                  </Grid>
                  <Grid container style={{marginTop: 10}}>
                    <Item xs={3}>
                      <ConfigurationDatePicker
                        path={`${path}:overrideStartDate`}
                      />
                    </Item>
                    <Item xs={3}>
                      <ConfigurationDatePicker
                        path={`${path}:overrideFinishDate`}
                      />
                    </Item>
                  </Grid>
                </Paper>

                <hr/>

                <Paper className={classes.paper}>
                  <Typography variant="body2" color="textSecondary">
                    <h6>Separator/ Hierarchy Column Options</h6>
                  </Typography>

                  <Typography variant="caption" color="textSecondary">
                    Use this option to control the separator used in the P6-Loader exports:
                  </Typography>

                  <Grid container style={{marginTop: 10}}>
                    <Item xs={6}>
                      <ConfigurationTextField path={`${path}:exportSeparator`}/>
                    </Item>
                  </Grid>

                  <Typography variant="caption" color="textSecondary">
                    Use the 'Full or Classic' option to control which columns are shown as required in the extracted Excel file. If the [Full] option is selected, then the "FullPath" column
                    is shown as required, and the "Parent"/ "Code" columns are shown as read-only. Choosing the [Classic] option does the opposite. This only affects the formatting of the columns.
                    <p/>
                    You can also hide the columns completely if you want. Hiding both columns is not possible, and the column corresponding to the preferred option selected here will be shown.
                  </Typography>

                  <Grid container style={{marginTop: 10}}>
                    <Item xs={4}>
                      <ConfigurationComboBox path={`${path}:fullOrClassicLoadDynamicOptionPreferred`}/>
                    </Item>
                    <Item xs={4}>
                      <ConfigurationCheckBox path={`${path}:exportFullPathColumn`}/>
                    </Item>
                    <Item xs={4}>
                      <ConfigurationCheckBox path={`${path}:exportParentCodeColumns`}/>
                    </Item>
                  </Grid>
                </Paper>
              </div>
            </AccordionDetails>
          </Accordion>
          <p/>
          <Paper className={classes.paper}>
            <Typography variant="body2" color="textSecondary">
              <h6>Select the modules and projects to extract</h6>
            </Typography>
            <div className={classes.paper}>
              <Grid
                container
                spacing={1}
                justify="center"
                className={classes.root}
                alignItems="flex-start"
              >
                <Grid item xs={5}>
                  <ModuleTreeView path={"loader:extract:modules"}/>
                </Grid>
                <Grid item xs={5}>
                  <TreeView
                    allowRefresh={true}
                    path={"loader:extract:projects"}
                    url={"loader/epsTree"}
                  />
                </Grid>
              </Grid>
            </div>
          </Paper>
        </div>
      </div>
    </React.Fragment>
  );
};
export default ExtractPage;
